/**
 * Browser Web Speech API (Chrome / Edge). Uses the engine built into the browser —
 * no app API keys or AI tokens. Quality depends on the browser and microphone.
 *
 * Optional: set a dialect explicitly:
 *   localStorage.setItem("manage360_speech_lang", "en-IN");
 * Common choices: en-IN, en-US, en-GB, hi-IN.
 */

import { cleanupPortfolioSpeechTranscript } from "./speechTranscriptCleanup";

const STORAGE_KEY = "manage360_speech_lang";

/** Default pause before ending a continuous dictation session (Portfolio). */
export const VOICE_DICTATION_SILENCE_MS = 7000;

const FINANCE_BIAS_TERMS = [
  "rupee",
  "rupees",
  "inr",
  "salary",
  "hdfc",
  "sbi",
  "icici",
  "bank",
  "credit",
  "card",
  "investment",
  "investments",
  "fd",
  "fixed",
  "deposit",
  "lakh",
  "lakhs",
  "thousand",
  "cash",
  "in",
  "out",
  "spent",
  "received",
  "paid",
  "transaction",
  "upi",
  "may",
];

export function getSpeechRecognitionClass() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function getSpeechGrammarListClass() {
  if (typeof window === "undefined") return null;
  return window.SpeechGrammarList || window.webkitSpeechGrammarList || null;
}

/**
 * Prefer saved dialect, else Indian English (best for INR / Hinglish money phrases).
 */
export function getPreferredSttLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && String(saved).trim()) return saved.trim();
  } catch {
    /* ignore */
  }
  return "en-IN";
}

function uniqueTerms(list) {
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    const t = String(raw || "")
      .trim()
      .toLowerCase();
    if (!t || t.length < 2 || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function buildBiasTermList(biasTerms, categoryNames) {
  const fromCategories = (categoryNames || []).flatMap((name) =>
    String(name || "")
      .toLowerCase()
      .split(/[^a-z0-9]+/i)
      .filter((w) => w.length >= 3)
  );
  return uniqueTerms([...FINANCE_BIAS_TERMS, ...biasTerms, ...fromCategories]);
}

/**
 * Optional JSGF grammar — nudges Chrome toward portfolio folder names (no tokens).
 */
function applySpeechGrammar(rec, terms) {
  const GrammarList = getSpeechGrammarListClass();
  if (!GrammarList || !terms.length) return false;
  const limited = terms.slice(0, 120);
  const body = limited.map((t) => t.replace(/[^a-z0-9']/gi, "")).filter(Boolean);
  if (!body.length) return false;
  try {
    const grammar = `#JSGF V1.0; grammar portfolio; public <pterm> = ${body.join(
      " | "
    )} ;`;
    const list = new GrammarList();
    list.addFromString(grammar, 1);
    rec.grammars = list;
    return true;
  } catch {
    return false;
  }
}

function scoreAlternative(alt, biasTerms) {
  const transcript = String(alt?.transcript || "").trim();
  if (!transcript) return -1;
  let score =
    typeof alt.confidence === "number" ? alt.confidence * 100 : 50;
  const lower = transcript.toLowerCase();
  if (/\d/.test(transcript)) score += 12;
  if (/\brupee|rupees|inr|₹/i.test(transcript)) score += 8;
  if (/\blakh|thousand|hundred|crore/i.test(transcript)) score += 6;
  for (const term of biasTerms) {
    if (term.length >= 3 && lower.includes(term)) score += 4;
  }
  if (transcript.length > 8) score += 2;
  return score;
}

/**
 * Pick the best hypothesis using confidence + finance/portfolio term fit.
 */
export function pickBestAlternative(result, biasTerms = []) {
  if (!result || result.length === 0) return "";
  let best = result[0];
  let bestScore = scoreAlternative(best, biasTerms);
  for (let i = 1; i < result.length; i++) {
    const alt = result[i];
    const s = scoreAlternative(alt, biasTerms);
    if (s > bestScore) {
      best = alt;
      bestScore = s;
    }
  }
  return String(best.transcript || "").trim();
}

function buildTranscriptFromResults(results, biasTerms) {
  let finalText = "";
  let interimText = "";
  for (let i = 0; i < results.length; i++) {
    const res = results[i];
    const piece = pickBestAlternative(res, biasTerms);
    if (!piece) continue;
    if (res.isFinal) finalText += `${piece} `;
    else interimText += piece;
  }
  return {
    finalText: finalText.trim(),
    interimText: interimText.trim(),
  };
}

function joinTranscriptParts(...parts) {
  return parts
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function applyCleanup(text, cleanupFn, categoryNames) {
  if (!text) return "";
  if (typeof cleanupFn === "function") {
    return cleanupFn(text);
  }
  return cleanupPortfolioSpeechTranscript(text, { categoryNames });
}

/**
 * Dictation with interim updates. Default: one-shot (~2s browser pause ends session).
 *
 * @param {object} opts
 * @param {boolean} [opts.continuous=false]
 * @param {number} [opts.silenceTimeoutMs=0]
 * @param {string[]} [opts.biasTerms]
 * @param {string[]} [opts.categoryNames] — folder names for grammar + cleanup
 * @param {(text: string) => string} [opts.cleanupTranscript]
 * @returns {{ abort: () => void, stopAndCapture?: () => void }}
 */
export function startBrowserVoiceDictation({
  lang,
  onInterim,
  onFinal,
  onError,
  onListeningStart,
  onListeningEnd,
  continuous = false,
  silenceTimeoutMs = 0,
  biasTerms = [],
  categoryNames = [],
  cleanupTranscript,
}) {
  const Ctor = getSpeechRecognitionClass();
  if (!Ctor) {
    onError?.({ error: "unsupported" });
    return { abort: () => {} };
  }

  const useExtendedSession =
    continuous && Number(silenceTimeoutMs) > 0;
  const mergedBias = buildBiasTermList(biasTerms, categoryNames);
  const clean = (t) =>
    applyCleanup(t, cleanupTranscript, categoryNames);

  const rec = new Ctor();
  rec.lang = lang || getPreferredSttLang();
  rec.interimResults = true;
  rec.continuous = useExtendedSession ? true : continuous;
  rec.maxAlternatives = 10;

  applySpeechGrammar(rec, mergedBias);

  let lastDraft = "";
  let committedText = "";
  let aborted = false;
  let sessionActive = true;
  let endedBySilence = false;
  let listeningEndedFired = false;
  let lastErrorCode = null;
  let silenceTimer = null;
  let startAttempted = false;
  let finalDelivered = false;

  const clearSilenceTimer = () => {
    if (silenceTimer) {
      clearTimeout(silenceTimer);
      silenceTimer = null;
    }
  };

  const fireListeningEndOnce = () => {
    if (listeningEndedFired) return;
    listeningEndedFired = true;
    onListeningEnd?.();
  };

  const deliverFinalOnce = (text) => {
    if (finalDelivered) return;
    finalDelivered = true;
    fireListeningEndOnce();
    onFinal?.(clean(text));
  };

  const finishSession = (text) => {
    clearSilenceTimer();
    sessionActive = false;
    deliverFinalOnce(text);
  };

  const resetSilenceTimer = () => {
    if (!useExtendedSession || !sessionActive || aborted) return;
    clearSilenceTimer();
    silenceTimer = setTimeout(() => {
      endedBySilence = true;
      sessionActive = false;
      clearSilenceTimer();
      try {
        rec.stop();
      } catch {
        finishSession(lastDraft);
      }
    }, silenceTimeoutMs);
  };

  const updateDraftFromEvent = (event) => {
    const { finalText, interimText } = buildTranscriptFromResults(
      event.results,
      mergedBias
    );
    if (useExtendedSession) {
      if (finalText) {
        committedText = joinTranscriptParts(committedText, finalText);
      }
      lastDraft = joinTranscriptParts(committedText, interimText);
    } else {
      lastDraft = joinTranscriptParts(finalText, interimText);
    }
    const cleaned = clean(lastDraft);
    if (cleaned) onInterim?.(cleaned);
    resetSilenceTimer();
  };

  const tryStartRecognition = () => {
    if (aborted || !sessionActive) return;
    try {
      rec.start();
      startAttempted = true;
      resetSilenceTimer();
    } catch (e) {
      if (startAttempted) {
        finishSession(lastDraft);
      } else {
        fireListeningEndOnce();
        onError?.({ error: "start-failed", nativeError: e });
      }
    }
  };

  rec.onresult = (event) => {
    updateDraftFromEvent(event);
  };

  rec.onerror = (ev) => {
    lastErrorCode = ev?.error || "unknown";
    if (lastErrorCode === "aborted") return;
    if (
      useExtendedSession &&
      (lastErrorCode === "no-speech" || lastErrorCode === "network")
    ) {
      return;
    }
    clearSilenceTimer();
    sessionActive = false;
    if (lastDraft.trim() && !aborted) {
      finishSession(lastDraft);
      return;
    }
    fireListeningEndOnce();
    onError?.(ev);
  };

  rec.onend = () => {
    if (aborted) {
      clearSilenceTimer();
      fireListeningEndOnce();
      return;
    }

    if (useExtendedSession && sessionActive && !endedBySilence) {
      window.setTimeout(() => tryStartRecognition(), 200);
      return;
    }

    const code = lastErrorCode;
    lastErrorCode = null;

    if (
      !useExtendedSession &&
      (code === "no-speech" ||
        code === "audio-capture" ||
        code === "not-allowed")
    ) {
      finishSession("");
      return;
    }

    finishSession(lastDraft);
  };

  onListeningStart?.();
  tryStartRecognition();

  return {
    abort: () => {
      aborted = true;
      sessionActive = false;
      clearSilenceTimer();
      try {
        rec.abort();
      } catch {
        try {
          rec.stop();
        } catch {
          fireListeningEndOnce();
        }
      }
    },
    stopAndCapture: () => {
      sessionActive = false;
      endedBySilence = true;
      clearSilenceTimer();
      try {
        rec.stop();
      } catch {
        deliverFinalOnce(lastDraft);
      }
    },
  };
}
